import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Typography,
  OutlinedInput,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { PlusIcon } from '../../../../../../assets/icons';
import { WidgetListDialog } from '../../../../../../components/Dialogs/WidgetList';
import { ContentWidget, WidgetProps } from '../index';
import widgetStyles from '../styles';

const COLUMN_WIDTH_MIN = 20;
const COLUMN_WIDTH_MAX = 80;

const PRESETS: { label: string; widths: number[] }[] = [
  { label: '50 / 50', widths: [50, 50] },
  { label: '80 / 20', widths: [80, 20] },
  { label: '20 / 80', widths: [20, 80] },
];

/** Types that must not nest inside a layout row (keeps layout simple + avoids recursion). */
const EXCLUDE_FROM_ROW = ['row', 'custom-portfolio'];

type Column = {
  width: number;
  widget: { id: number; type: string; content: any } | null;
};

type RowContent = {
  gap?: number;
  columns: Column[];
};

function clampColumnWidth(n: number): number {
  return Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(n)));
}

function normalizeColumns(content: any): Column[] {
  const cols = Array.isArray(content?.columns) ? content.columns : [];
  if (cols.length === 0) {
    return [
      { width: 50, widget: null },
      { width: 50, widget: null },
    ];
  }
  // Always two columns; migrate legacy single-column (100%) rows.
  const pair =
    cols.length === 1 ? [cols[0], { width: 50, widget: null }] : cols.slice(0, 2);
  return pair.map((c: any) => ({
    width:
      typeof c?.width === 'number' && Number.isFinite(c.width) ? clampColumnWidth(c.width) : 50,
    widget: c?.widget && typeof c.widget === 'object' ? c.widget : null,
  }));
}

export const LayoutRow = ({
  id,
  content,
  preview,
  themeColor,
  isin,
  onChange,
  originalContent,
  tabWidgets,
  onAdoptTabWidgetIntoColumn,
  onEjectColumnWidgetToTab,
}: Omit<WidgetProps, 'type'>) => {
  const [openDialogFor, setOpenDialogFor] = useState<number | null>(null);
  const columns = normalizeColumns(content);
  const gap = typeof content?.gap === 'number' && Number.isFinite(content.gap) ? content.gap : 24;

  const usedInRowIds = new Set(
    columns.map((c) => c.widget?.id).filter((x): x is number => x != null)
  );

  const adoptCandidates = (Array.isArray(tabWidgets) ? tabWidgets : []).filter(
    (w) =>
      w &&
      w.id != null &&
      w.id !== id &&
      !usedInRowIds.has(w.id) &&
      !EXCLUDE_FROM_ROW.includes(w.type)
  );

  const emit = (next: RowContent) => {
    onChange(id, next);
  };

  const updateColumns = (nextColumns: Column[]) => {
    emit({ gap, columns: nextColumns });
  };

  const applyPreset = (widths: number[]) => {
    const next: Column[] = widths.map((width, i) => ({
      width: clampColumnWidth(width),
      widget: columns[i]?.widget ?? null,
    }));
    updateColumns(next);
  };

  const handleWidthChange = (index: number, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    updateColumns(
      columns.map((col, i) => (i === index ? { ...col, width: clampColumnWidth(n) } : col))
    );
  };

  const handleGapChange = (raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return;
    emit({ gap: n, columns });
  };

  const handleSelect = (selected: any) => {
    if (openDialogFor == null) return;
    if (EXCLUDE_FROM_ROW.includes(selected?.type)) {
      setOpenDialogFor(null);
      return;
    }
    updateColumns(
      columns.map((col, i) => (i === openDialogFor ? { ...col, widget: selected } : col))
    );
    setOpenDialogFor(null);
  };

  const handleAdoptExisting = (widgets: any[]) => {
    if (openDialogFor == null || !widgets?.length || id == null) return;
    const chosen = widgets[0];
    if (!chosen?.id || !onAdoptTabWidgetIntoColumn) return;
    onAdoptTabWidgetIntoColumn(id, openDialogFor, chosen.id);
    setOpenDialogFor(null);
  };

  const handleChangeChild = (widgetId: number | undefined, newContentData: any) => {
    updateColumns(
      columns.map((col) => {
        if (!col.widget || col.widget.id !== widgetId) return col;
        return {
          ...col,
          widget: {
            id: col.widget.id,
            type: col.widget.type,
            content: newContentData,
          },
        };
      })
    );
  };

  const handleClearColumn = (index: number) => {
    updateColumns(columns.map((col, i) => (i === index ? { ...col, widget: null } : col)));
  };

  const handleMoveOutside = (index: number) => {
    if (id == null || !onEjectColumnWidgetToTab || !columns[index]?.widget) return;
    onEjectColumnWidgetToTab(id, index);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
      {!preview && (
        <Box sx={{ mb: 12 }}>
          <Typography variant="h6" sx={{ mb: 8 }}>
            2-column layout
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 8 }}>
            Optional row for website widgets. Existing widgets without this type are unchanged.
            Empty columns still reserve space on desktop (like SQUAD portfolio).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, mb: 8 }}>
            <Typography variant="caption" sx={{ minWidth: 40 }}>
              Preset
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              {PRESETS.map((p) => (
                <Button key={p.label} onClick={() => applyPreset(p.widths)}>
                  {p.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 12, mb: 12 }}>
            <Typography variant="caption" sx={{ minWidth: 40 }}>
              Gap (px)
            </Typography>
            <OutlinedInput
              type="number"
              size="small"
              value={gap}
              onChange={(e) => handleGapChange(e.target.value)}
              sx={{ width: 100 }}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: preview ? 'row' : 'column',
          gap: preview ? `${gap}px` : 16,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          '@media (max-width: 991px)': preview
            ? { flexDirection: 'column' }
            : undefined,
        }}
      >
        {columns.map((col, index) => (
          <Box
            key={index}
            sx={{
              ...(preview
                ? {
                    flex: `0 0 ${col.width}%`,
                    maxWidth: `${col.width}%`,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    '@media (max-width: 991px)': {
                      flex: '1 1 auto',
                      maxWidth: '100%',
                      width: '100%',
                    },
                  }
                : {
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    border: '1px dashed #c5c5c5',
                    borderRadius: 1,
                    padding: '12px',
                    backgroundColor: '#fafafa',
                  }),
            }}
          >
            {!preview && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, mb: 8 }}>
                <Typography variant="subtitle2">Column {index + 1}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Width %
                </Typography>
                <OutlinedInput
                  type="number"
                  size="small"
                  value={col.width}
                  onChange={(e) => handleWidthChange(index, e.target.value)}
                  sx={{ width: 80 }}
                  inputProps={{ min: COLUMN_WIDTH_MIN, max: COLUMN_WIDTH_MAX }}
                />
                {col.widget && (
                  <IconButton size="small" onClick={() => handleClearColumn(index)} title="Clear column">
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}

            {col.widget ? (
              <Box
                sx={(theme) =>
                  preview
                    ? { width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }
                    : {
                        ...widgetStyles.widget(theme, { preview }),
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }
                }
              >
                <ContentWidget
                  {...col.widget}
                  isin={isin}
                  originalContent={originalContent?.columns?.[index]?.widget}
                  themeColor={themeColor}
                  preview={preview}
                  onChange={handleChangeChild}
                />
              </Box>
            ) : (
              !preview && (
                <Button
                  variant="outlined"
                  fullWidth
                  sx={(theme) => ({
                    ...widgetStyles.widgetButton(theme),
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    marginBottom: 0,
                  })}
                  startIcon={<PlusIcon />}
                  onClick={() => setOpenDialogFor(index)}
                >
                  Add widget to column {index + 1}
                </Button>
              )
            )}

            {!preview && col.widget && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 8, mt: 4 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setOpenDialogFor(index)}
                >
                  Replace widget
                </Button>
                {onEjectColumnWidgetToTab && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => handleMoveOutside(index)}
                  >
                    Move outside layout
                  </Button>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {openDialogFor != null && (
        <WidgetListDialog
          open
          excludeTypes={EXCLUDE_FROM_ROW}
          onSelect={handleSelect}
          onClose={() => setOpenDialogFor(null)}
          existingWidgets={adoptCandidates}
          onWrapExisting={onAdoptTabWidgetIntoColumn ? handleAdoptExisting : undefined}
          maxWrapSelection={1}
        />
      )}
    </Box>
  );
};
